import type {
  RandomSelectionStrategy,
  WeightedActionItem,
  StateGroupConfig,
  StateMappingItem,
} from './weightedStateUtils';
import {
  normalizeStateGroupConfig,
  stateGroupConfigToManifestItem,
  calculateNormalizedWeights,
  pickWeightedItem,
} from './weightedStateUtils';

export type {
  RandomSelectionStrategy,
  WeightedActionItem,
  StateGroupConfig,
  StateMappingItem,
};

export {
  normalizeStateGroupConfig,
  stateGroupConfigToManifestItem,
  calculateNormalizedWeights,
  pickWeightedItem,
};

export type ExtensionTemplateType = 'spine2d' | 'image-states' | 'custom-html';

export function normalizeExtensionTemplateType(template?: string | null): ExtensionTemplateType {
  const normalized = (template || '').toLowerCase().trim();
  if (normalized === 'spine' || normalized === 'spine2d') return 'spine2d';
  if (normalized === 'custom-html' || normalized === 'html' || normalized === 'iframe') return 'custom-html';
  return 'image-states';
}

export interface SpineFilesConfig {
  skeleton?: string;
  atlas?: string;
  texture?: string;
  [key: string]: string | undefined;
}

export interface ExtensionSettings {
  premultipliedAlpha?: boolean;
  scale?: number;
  width?: number | string;
  height?: number | string;
  backgroundColor?: string;
  sleepTimeout?: number;
}

export interface BaseExtensionManifest {
  version?: string;
  settings?: ExtensionSettings;
}

export interface SpineExtensionManifest extends BaseExtensionManifest {
  template: 'spine2d' | 'spine';
  files?: SpineFilesConfig;
  states?: {
    idle?: StateMappingItem;
    sleep?: StateMappingItem;
    thinking?: StateMappingItem;
    speaking?: StateMappingItem;
    error?: StateMappingItem;
    click?: StateMappingItem;
    [key: string]: StateMappingItem | undefined;
  };
}

export interface ImageStatesExtensionManifest extends BaseExtensionManifest {
  template: 'image-states';
  states?: {
    idle?: StateMappingItem;
    sleep?: StateMappingItem;
    thinking?: StateMappingItem;
    speaking?: StateMappingItem;
    error?: StateMappingItem;
    click?: StateMappingItem;
    [key: string]: StateMappingItem | undefined;
  };
}

export interface CustomHtmlExtensionManifest extends BaseExtensionManifest {
  template: 'custom-html' | 'html' | 'iframe';
  entry?: string;
}

export type ExtensionManifest =
  | SpineExtensionManifest
  | ImageStatesExtensionManifest
  | CustomHtmlExtensionManifest;

export type SimulatedState = 'idle' | 'sleep' | 'thinking' | 'speaking' | 'error' | 'click';

export interface ExtensionManifestFormValues {
  templateType: ExtensionTemplateType;

  // Spine fields
  skeletonFile: string | null;
  atlasFile: string | null;
  spineIdleConfig: StateGroupConfig;
  spineSleepConfig: StateGroupConfig;
  spineThinkingConfig: StateGroupConfig;
  spineSpeakingConfig: StateGroupConfig;
  spineErrorConfig: StateGroupConfig;
  spineClickConfig: StateGroupConfig;

  // Image states fields
  imageIdleConfig: StateGroupConfig;
  imageSleepConfig: StateGroupConfig;
  imageThinkingConfig: StateGroupConfig;
  imageSpeakingConfig: StateGroupConfig;
  imageErrorConfig: StateGroupConfig;
  imageClickConfig: StateGroupConfig;

  // Custom HTML fields
  entryHtml: string | null;

  // Common settings
  width: string;
  height: string;
  sleepTimeout?: number;
}

export function getDefaultManifestFormValues(
  defaults?: Partial<ExtensionManifestFormValues>
): ExtensionManifestFormValues {
  return {
    templateType: 'image-states',
    skeletonFile: null,
    atlasFile: null,
    spineIdleConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [{ animation: 'idle', weight: 1, loop: true }],
    },
    spineSleepConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [{ animation: 'sleep', weight: 1, loop: true }],
    },
    spineThinkingConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [{ animation: 'think', weight: 1, loop: true }],
    },
    spineSpeakingConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [{ animation: 'talk', weight: 1, loop: true }],
    },
    spineErrorConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [{ animation: 'confused', weight: 1, loop: true }],
    },
    spineClickConfig: {
      strategy: 'on-state-enter',
      interval: 5,
      items: [{ animation: 'wave', weight: 1, loop: false }],
    },
    imageIdleConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [],
    },
    imageSleepConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [],
    },
    imageThinkingConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [],
    },
    imageSpeakingConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [],
    },
    imageErrorConfig: {
      strategy: 'on-complete',
      interval: 5,
      items: [],
    },
    imageClickConfig: {
      strategy: 'on-state-enter',
      interval: 1.5,
      items: [],
    },
    entryHtml: 'index.html',
    width: '280px',
    height: '360px',
    sleepTimeout: 60,
    ...defaults,
  };
}

export function manifestToFormValues(
  manifest: ExtensionManifest,
  defaults?: Partial<ExtensionManifestFormValues>
): ExtensionManifestFormValues {
  const templateType = normalizeExtensionTemplateType(manifest.template);
  const commonSettings = manifest.settings;

  const res: ExtensionManifestFormValues = {
    ...getDefaultManifestFormValues(defaults),
    templateType,
    width: commonSettings?.width !== undefined ? String(commonSettings.width) : '280px',
    height: commonSettings?.height !== undefined ? String(commonSettings.height) : '360px',
    sleepTimeout: commonSettings?.sleepTimeout !== undefined ? commonSettings.sleepTimeout : 60,
  };

  if (templateType === 'spine2d') {
    const spineM = manifest as SpineExtensionManifest;
    if (spineM.files?.skeleton) res.skeletonFile = spineM.files.skeleton;
    if (spineM.files?.atlas) res.atlasFile = spineM.files.atlas;
    if (spineM.states) {
      const idleCfg = normalizeStateGroupConfig(spineM.states.idle, 'on-complete', 5, true);
      if (idleCfg.items.length > 0) res.spineIdleConfig = idleCfg;

      const sleepCfg = normalizeStateGroupConfig(spineM.states.sleep, 'on-complete', 5, true);
      if (sleepCfg.items.length > 0) res.spineSleepConfig = sleepCfg;

      const thinkCfg = normalizeStateGroupConfig(spineM.states.thinking, 'on-complete', 5, true);
      if (thinkCfg.items.length > 0) res.spineThinkingConfig = thinkCfg;

      const speakCfg = normalizeStateGroupConfig(spineM.states.speaking, 'on-complete', 5, true);
      if (speakCfg.items.length > 0) res.spineSpeakingConfig = speakCfg;

      const errCfg = normalizeStateGroupConfig(spineM.states.error, 'on-complete', 5, true);
      if (errCfg.items.length > 0) res.spineErrorConfig = errCfg;

      const clickCfg = normalizeStateGroupConfig(spineM.states.click, 'on-state-enter', 5, false);
      if (clickCfg.items.length > 0) res.spineClickConfig = clickCfg;
    }
  } else if (templateType === 'image-states') {
    const imgM = manifest as ImageStatesExtensionManifest;
    if (imgM.states) {
      res.imageIdleConfig = normalizeStateGroupConfig(imgM.states.idle, 'on-complete', 5, true);
      res.imageSleepConfig = normalizeStateGroupConfig(imgM.states.sleep, 'on-complete', 5, true);
      res.imageThinkingConfig = normalizeStateGroupConfig(imgM.states.thinking, 'on-complete', 5, true);
      res.imageSpeakingConfig = normalizeStateGroupConfig(imgM.states.speaking, 'on-complete', 5, true);
      res.imageErrorConfig = normalizeStateGroupConfig(imgM.states.error, 'on-complete', 5, true);
      res.imageClickConfig = normalizeStateGroupConfig(imgM.states.click, 'on-state-enter', 1.5, false);
    }
  } else if (templateType === 'custom-html') {
    const htmlM = manifest as CustomHtmlExtensionManifest;
    if (htmlM.entry) res.entryHtml = htmlM.entry;
  }

  return res;
}

export function formValuesToManifest(values: ExtensionManifestFormValues): ExtensionManifest {
  const settings: ExtensionSettings = {
    width: values.width || undefined,
    height: values.height || undefined,
    sleepTimeout: typeof values.sleepTimeout === 'number' ? values.sleepTimeout : undefined,
  };

  if (values.templateType === 'spine2d') {
    const manifest: SpineExtensionManifest = {
      template: 'spine2d',
      version: '1.0',
      settings,
      files: {
        skeleton: values.skeletonFile || undefined,
        atlas: values.atlasFile || undefined,
      },
      states: {
        idle: stateGroupConfigToManifestItem(values.spineIdleConfig, 'on-complete', 5),
        sleep: stateGroupConfigToManifestItem(values.spineSleepConfig, 'on-complete', 5),
        thinking: stateGroupConfigToManifestItem(values.spineThinkingConfig, 'on-complete', 5),
        speaking: stateGroupConfigToManifestItem(values.spineSpeakingConfig, 'on-complete', 5),
        error: stateGroupConfigToManifestItem(values.spineErrorConfig, 'on-complete', 5),
        click: stateGroupConfigToManifestItem(values.spineClickConfig, 'on-state-enter', 5),
      },
    };
    return manifest;
  }

  if (values.templateType === 'image-states') {
    const manifest: ImageStatesExtensionManifest = {
      template: 'image-states',
      version: '1.0',
      settings,
      states: {
        idle: stateGroupConfigToManifestItem(values.imageIdleConfig, 'on-complete', 5),
        sleep: stateGroupConfigToManifestItem(values.imageSleepConfig, 'on-complete', 5),
        thinking: stateGroupConfigToManifestItem(values.imageThinkingConfig, 'on-complete', 5),
        speaking: stateGroupConfigToManifestItem(values.imageSpeakingConfig, 'on-complete', 5),
        error: stateGroupConfigToManifestItem(values.imageErrorConfig, 'on-complete', 5),
        click: stateGroupConfigToManifestItem(values.imageClickConfig, 'on-state-enter', 1.5),
      },
    };
    return manifest;
  }

  const manifest: CustomHtmlExtensionManifest = {
    template: 'custom-html',
    version: '1.0',
    entry: values.entryHtml || 'index.html',
    settings,
  };
  return manifest;
}
